<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <div
    v-else-if="!computedTextProps.is_removed"
    class="cb-text-component relative"
    :class="{ 'rounded-2': isEditMode }"
    :style="parentStyle"
  >
    <EditableComponent
      v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :class="{
        '-translate-y-[56px]': isSelected && isScribbleChild && editorComponent?.toolbarOnTop,
      }"
      :collapsible="!isScribbleChild"
      :discard="shouldShowDiscardOption"
      :duplicate="componentPermissions.canDuplicate"
      :exclude-stylables="
        isScribbleChild ? [ThemeComponentOptionEnum.SPACING, ThemeComponentOptionEnum.BORDER_RADIUS] : undefined
      "
      :parent-type="parentType"
      :remove="componentPermissions.canRemove"
      :resizable="isScribbleChild && !usedInSectionId"
      :resize-handles="['center-left', 'center-right']"
      :selected="isSelected"
      :settings="!usedInSectionId"
      :show-only-bottom-toolbar="shouldShowOnlyBottomToolbar"
      :show-only-top-toolbar="shouldShowOnlyTopToolbar"
      :stylable="!usedInSectionId"
      :style="editableComponentTextStyle"
      @discard="discard()"
      @duplicate="duplicate(id)"
      @remove="removeComponent(id, usedInSectionId)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <CRichTextEditor
        :id="id"
        ref="editorComponent"
        :allowEdit="componentPermissions.canEdit"
        class="c-text__rich-text-editor"
        :class="{ 'z-3': launchDarkly.disable_z_index_experiments }"
        :flexible-toolbar="parentType === 'Scribble'"
        :modelValue="memoizedModelValue"
        :parent-type="isScribbleChild ? ComponentTypes.Scribble : undefined"
        :showMenu="isSelected"
        :style="richTextEditorStyle"
        @focus="onFocus"
        @keydown="onKeyDown"
        @keydown.tab.prevent
        @keyup.stop
        @openFile="(payload) => onFileOpen({ file_id: payload.fileId, page_index: payload.pageIndex })"
        @update:model-value="handleTextUpdate"
      />
    </EditableComponent>
    <div
      v-else
      ref="contentElement"
      class="canvas-text"
      :style="presentationModeStyle"
      @click="handleContentClick"
      v-html="memoizedModelValue"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs, inject, watch, type ComputedRef, type CSSProperties, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { omit, pick, debounce } from 'lodash-es'
import {
  ComponentTypes,
  type CanvasBuilderProps,
  type ComponentNode,
  type TextProps,
  type ComponentNodeThemeMeta,
  type CanvasTheme,
} from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasSectionOverrides from '@canvas-builder/composables/useCanvasSectionOverrides'
import CRichTextEditor from '@lib/components/CRichTextEditor/CRichTextEditor.vue'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { useComponentStyle } from '@canvas-builder/composables/useComponentStyle'
import { discardSectionComponentOverride, shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import { type CanvasRetrieve } from '@openapi'
import useConfirmation from '@lib/composables/useConfirmation'
import { renderTemplate } from '@lib/util/handlebars'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { handleContentLinkClick } from '@lib/components/CRichTextEditor/extensions/contentLink'
import type { BlockContext } from '@canvas-builder/components/ui/Block/Block.types'
import useCrmShapeWithSectionContext from '@canvas-builder/composables/useCrmShapeWithSectionContext'
import useCanvasTheme, { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import usePrintMode from '@canvas-builder/composables/usePrintMode'
import { useCoreApi } from '@lib/composables/useCoreApi'
import type { CanvasMetadataRetrieve } from '@lib/types/canvases'
import {
  setupTableSorting,
  resetTableStructureSnapshot,
  type TextComponentMetadata,
} from '@canvas-builder/util/tiptap-table.util'
import { useScribbleChild, getTextScribbleConfig } from '@canvas-builder/composables/useScribbleChild'
import { useScribbleToolbar } from '@canvas-builder/composables/useScribbleToolbar'

const openExternalUrl = inject<CanvasBuilderProps['onOpenExternalUrl']>('openExternalUrl')

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: TextProps
    parentType?: ComponentTypes
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: CSSProperties
    theme_meta?: ComponentNodeThemeMeta
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties]
}>()
const blockCtx = inject<BlockContext>(
  'usedInBlockCtx',
  computed(() => ({})),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const sectionTheme = computed(() => {
  return usedInSectionId.value ? (activeCanvasSections.value[usedInSectionId.value]?.theme as CanvasTheme) : undefined
})

const parentStyle = computed(() => {
  const style = {
    ...getDerivedStyleFromFeatureFlags(props.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Text, props.theme_meta, sectionTheme.value),
  }

  const baseOmits = ['paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight']

  if (props.usedInSectionId && isScribbleChild.value && !componentPermissions.value.hasSomethingEditable) {
    return omit(style, [...baseOmits, 'position', 'left', 'right', 'top', 'bottom'])
  }

  return isEditMode.value ? omit(style, baseOmits) : undefined
})

const editableComponentTextStyle = computed(() => {
  const style = {
    ...getDerivedStyleFromFeatureFlags(props.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Text, props.theme_meta, sectionTheme.value),
  }
  return pick(style, 'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomLeftRadius', 'borderBottomRightRadius')
})

const richTextEditorStyle = computed(() => {
  const style = {
    ...getDerivedStyleFromFeatureFlags(props.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Text, props.theme_meta, sectionTheme.value),
  }
  return pick(
    style,
    'paddingBottom',
    'paddingLeft',
    'paddingTop',
    'paddingRight',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
  )
})

const presentationModeStyle = computed(() => {
  const style = {
    ...getDerivedStyleFromFeatureFlags(props.style),
    ...getCssThemeMetaStyleOverrides(ComponentTypes.Text, props.theme_meta, sectionTheme.value),
  }
  return !isEditMode.value || props.usedInSectionId ? style : undefined
})

const shouldInterpolate = computed((): boolean => {
  if (!activeCanvas.value?.context) return false
  else if (activeCanvas.value?.is_template || activeCanvas.value?.is_section) return !isEditMode.value
  return launchDarkly.value?.interpolate_rep_canvases_on_edit_mode || !isEditMode.value
})
const editorComponent = ref<InstanceType<typeof CRichTextEditor>>()
const contentElement = ref<HTMLElement | null>(null)
const { t } = useI18n()
const confirmation = useConfirmation()
const {
  activeCanvas,
  activeCanvasSections,
  componentSelectedMode,
  duplicate,
  isEditMode,
  isPrintDisplayMode,
  isViewOnlyMode,
  justAddedComponentId,
  mode,
  removeComponent,
  sectionComponentOverrides,
  setComponentSelectedMode,
  setComponentSettingsMode,
  updateNodeData,
  onFileOpen,
  canvasMetadata,
  setCanvasMetadata,
  isImpact,
} = useCanvas()

// Use the new composable for context building
const { fullContext } = useCrmShapeWithSectionContext()
const { coreRestApiAxios } = useCoreApi()
const { applyPrintModeToElement } = usePrintMode()
const { discardOverridesConfirmationOptions, getComponentOverrides } = useCanvasSectionOverrides()
const { getDerivedStyleFromFeatureFlags, getCssThemeMetaStyleOverrides } = useComponentStyle()
const { maintainSectionTheme } = useCanvasTheme()
const { data, id, usedInSectionId, when_used_in_section } = toRefs(props)
const isSelected = computed(() => componentSelectedMode.value && componentSelectedMode.value.id === id.value)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})
const scribbleChildConfig = getTextScribbleConfig(props.id, props.parentType, props.style)
const { isScribbleChild, handleAddedToScribble } = useScribbleChild(scribbleChildConfig)
const { shouldShowOnlyTopToolbar, shouldShowOnlyBottomToolbar } = useScribbleToolbar(props.id)

const computedTextProps = getComponentOverrides(data, id, usedInSectionId) as ComputedRef<{
  data: TextProps
  is_removed: boolean
}>

const shouldShowDiscardOption = computed(() => {
  return !!(props.usedInSectionId && sectionComponentOverrides.value?.[props.usedInSectionId]?.[props.id])
})

const isTableSortingEnabled = computed(() => {
  return launchDarkly.value?.enable_sorting_in_tiptap_table && !isEditMode.value
})

const discard = () => {
  confirmation.warning(
    discardOverridesConfirmationOptions(t, () => {
      try {
        if (!activeCanvas.value?.id) {
          throw new Error('CanvasRetrieve ID is not defined')
        }
        const updatedOverrides = discardSectionComponentOverride(
          props.usedInSectionId,
          props.id,
          sectionComponentOverrides.value,
        )

        // update section component overrides
        sectionComponentOverrides.value = updatedOverrides

        // force update editorComponent to re-render and display the default content
        editorComponent.value?.setContent((computedTextProps.value.data as TextProps)?.content)
      } catch (error) {
        console.error(error)
        return
      }
    }),
  )
}

const onFocus = () => {
  setComponentSelectedMode(props.id)
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    editorComponent.value?.blur?.()
  } else {
    event.stopPropagation()
  }
}

const memoizedModelValue = computed(() => getModelValue(computedTextProps.value.data?.content))

function getModelValue(value = '') {
  if (!shouldInterpolate.value || !value.trim()) return value

  return renderTemplate(value, fullContext.value)
}

const debouncedUpdateNodeData = debounce((data: TextProps, nodeId: string, sectionId?: string) => {
  return updateNodeData(data, nodeId, sectionId)
}, 100)

function handleHyperLinkClick(event: MouseEvent) {
  const target = event.target as HTMLElement
  const hyperLinkElement = target.matches('a[href]') ? target : target.closest('a[href]')
  if (hyperLinkElement) {
    event.preventDefault()
    const href = hyperLinkElement.getAttribute('href')
    if (href) {
      openExternalUrl?.({ url: href, target: '_blank' })
    }
    return true
  }
}

function handleContentClick(event: MouseEvent) {
  const openFile = (payload: { fileId: string; pageIndex?: number }) =>
    onFileOpen.value({ file_id: payload.fileId, page_index: payload.pageIndex })

  const handlers = [() => handleContentLinkClick(event, openFile), () => handleHyperLinkClick(event)]

  for (const handler of handlers) {
    if (handler()) {
      return
    }
  }
}

function handleTextUpdate(v?: string) {
  const value = v || ''

  if (blockCtx.value.usedInBlockId) {
    return blockCtx.value?.overrideBlockComponentData?.(props.id, { content: value })
  }

  if (isTableSortingEnabled.value && isEditMode.value) {
    resetTableStructureSnapshot(props.id)
  }

  const updateData = { ...(computedTextProps.value.data ?? {}), content: value }

  if (launchDarkly.value?.enable_improved_canvas_node_updating) {
    debouncedUpdateNodeData(updateData, props.id, props.usedInSectionId)
    return
  }

  return updateNodeData(updateData, props.id, props.usedInSectionId)
}

async function saveSortingMetadata(
  tableIndex: number,
  columnIndex: number,
  direction: 'asc' | 'desc' | 'original',
): Promise<void> {
  if (!isImpact.value || !coreRestApiAxios?.value || !activeCanvas.value?.id) {
    return
  }

  try {
    const currentComponentMetadata: TextComponentMetadata =
      canvasMetadata.value?.presentation_component_metadata?.[props.id] || {}

    const tableSorting = currentComponentMetadata.tableSorting || []

    const existingIndex = tableSorting.findIndex((item) => item.tableIndex === tableIndex)
    if (existingIndex >= 0) {
      tableSorting[existingIndex] = { tableIndex, columnIndex, direction }
    } else {
      tableSorting.push({ tableIndex, columnIndex, direction })
    }

    const updatedCanvasMetadata = await coreRestApiAxios.value.patch<unknown, CanvasMetadataRetrieve['config']>(
      `protected/canvases/${activeCanvas.value.id}/metadata`,
      {
        config: {
          presentation_component_metadata: {
            [props.id]: {
              ...currentComponentMetadata,
              tableSorting,
            },
          },
        },
      },
    )

    setCanvasMetadata(updatedCanvasMetadata.config)
  } catch (error) {
    console.error('[Text.vue]: Failed to update canvas metadata for table sorting', error)
  }
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    handleAddedToScribble(emit)
  }

  if (isPrintDisplayMode.value && contentElement.value) {
    setTimeout(() => {
      if (contentElement.value) {
        // Check if this text component should use section theme or canvas theme
        const shouldUseCanvasTheme =
          !maintainSectionTheme.value ||
          (props.usedInSectionId && activeCanvasSections.value[props.usedInSectionId]?.theme?.inherit_theme_from_parent)

        // Only apply canvas theme typography if the section should inherit from parent or maintain_section_theme is false
        if (shouldUseCanvasTheme) {
          // Only apply color preservation if the feature flag is enabled
          const enableScaleContent = !!launchDarkly.value.enable_scale_content

          // Extract span colors and their HTML locations before print transformation
          const spanStyles = enableScaleContent ? new Map() : null

          if (enableScaleContent) {
            contentElement.value.querySelectorAll('span').forEach((span, index) => {
              const el = span as HTMLElement
              // Only cache spans with explicit styling
              if (el.hasAttribute('style') && el.getAttribute('style')?.trim()) {
                const styleProps = {
                  color: el.style.color,
                  fontSize: el.style.fontSize,
                  fontWeight: el.style.fontWeight,
                  lineHeight: el.style.lineHeight,
                  letterSpacing: el.style.letterSpacing,
                  textTransform: el.style.textTransform,
                }
                // Mark the span with a data attribute to identify it
                const id = `span-${index}`
                el.setAttribute('data-style-id', id)
                spanStyles?.set(id, styleProps)
              }
            })
          }

          // Force the browser to compute critical styles before applying print transformations
          const style = window.getComputedStyle(contentElement.value)
          ;['color', 'fontFamily', 'fontSize', 'fontWeight', 'letterSpacing', 'lineHeight', 'textTransform'].forEach(
            (prop) => style[prop as any],
          )

          applyPrintModeToElement(contentElement.value, () => {
            const content = computedTextProps.value.data?.content || ''
            return shouldInterpolate.value ? renderTemplate(content, fullContext.value) : content
          })

          // Restore original styles after print transformation if feature flag is enabled
          if (enableScaleContent && spanStyles) {
            setTimeout(() => {
              spanStyles.forEach((styles, id) => {
                const span = contentElement.value?.querySelector(`[data-style-id="${id}"]`) as HTMLElement
                if (span) {
                  // Restore each style property that was originally set
                  Object.entries(styles).forEach(([prop, value]) => {
                    if (value) span.style[prop as any] = value as string
                  })
                }
              })
            }, 50)
          }
        }
        // If shouldUseCanvasTheme is false, the section's theme will be applied by the SectionList component
        // and we don't need to do anything here to avoid conflicts
      }
    }, 100)
  }

  if (isTableSortingEnabled.value && contentElement.value) {
    if (!isEditMode.value) {
      resetTableStructureSnapshot(props.id)
    }

    setTimeout(() => {
      if (contentElement.value) {
        setupTableSorting(contentElement.value, props.id, canvasMetadata, saveSortingMetadata)
      }
    }, 100)
  }
})

watch(
  () => props.data.content,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      editorComponent.value?.setContent(newValue)
    }
  },
)
</script>

<style lang="scss" scoped>
.canvas-text {
  :deep(p) {
    margin-bottom: 20px;
  }
}

:deep(.ProseMirror) {
  &:focus {
    outline: none;
  }
}

.cb-text-component :deep(p),
.c-text__rich-text-editor :deep(p),
.cb-text-component :deep(ol),
.c-text__rich-text-editor :deep(ol),
.cb-text-component :deep(ul),
.c-text__rich-text-editor :deep(ul) {
  margin: 0 0 1em 0;
  min-height: 1.6em; // to avoid empty p elements in presentation mode
}

.cb-text-component :deep(ol),
.c-text__rich-text-editor :deep(ol),
.cb-text-component :deep(ul),
.c-text__rich-text-editor :deep(ul) {
  padding: 0 0 0 1em;
}

.cb-text-component :deep(p:last-of-type),
.c-text__rich-text-editor :deep(p:last-of-type),
.cb-text-component :deep(ol:last-of-type),
.c-text__rich-text-editor :deep(ol:last-of-type),
.cb-text-component :deep(ul:last-of-type),
.c-text__rich-text-editor :deep(ul:last-of-type) {
  margin-bottom: 0em;
}

.cb-text-component :deep(p:last-of-type + ol),
.c-text__rich-text-editor :deep(p:last-of-type + ol),
.cb-text-component :deep(p:last-of-type + ul),
.c-text__rich-text-editor :deep(p:last-of-type + ul),
.cb-text-component :deep(ol:last-of-type + p),
.c-text__rich-text-editor :deep(ol:last-of-type + p),
.cb-text-component :deep(ol:last-of-type + ul),
.c-text__rich-text-editor :deep(ol:last-of-type + ul),
.cb-text-component :deep(ul:last-of-type + p),
.c-text__rich-text-editor :deep(ul:last-of-type + p),
.cb-text-component :deep(ul:last-of-type + ol),
.c-text__rich-text-editor :deep(ul:last-of-type + ol) {
  margin-top: 1em;
}

.cb-text-component :deep(ol > li ol),
.c-text__rich-text-editor :deep(ol > li ol),
.cb-text-component :deep(ol > li ul),
.c-text__rich-text-editor :deep(ol > li ul),
.cb-text-component :deep(ul > li ol),
.c-text__rich-text-editor :deep(ul > li ol),
.cb-text-component :deep(ul > li ul),
.c-text__rich-text-editor :deep(ul > li ul) {
  margin-top: 0 !important;
}

/* Sortable table styles */
.canvas-text :deep(.sortable-header) {
  position: relative;
}

.canvas-text :deep(.sortable-header::after) {
  content: '';
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.5;
}

.canvas-text :deep(.sortable-header:hover::after) {
  content: '↕';
  opacity: 0.7;
}

.canvas-text :deep(.sortable-header.sorted-asc::after) {
  content: '↑';
  opacity: 1;
}

.canvas-text :deep(.sortable-header.sorted-desc::after) {
  content: '↓';
  opacity: 1;
}

.canvas-text :deep(.sortable-header.sorted-original::after) {
  content: '';
}

.canvas-text :deep(.sortable-header.sorted-original:hover::after) {
  content: '↕';
  opacity: 0.5;
}
</style>
